import { apiClient } from '../client';

export interface Place {
  _id: string;
  place_id: string;
  name: string;
  address?: string;
  formatted_address?: string;
  formatted_phone_number?: string;
  international_phone_number?: string;
  website?: string;
  opening_hours?: {
    weekday_text?: string[];
    periods?: Array<{
      close?: { day: number; time: string };
      open?: { day: number; time: string };
    }>;
  };
  price_level?: number;
  rating?: number;
  user_ratings_total?: number;
  types?: string[];
  google_types?: string[];
  geometry?: {
    location: {
      lat: number;
      lng: number;
    };
  };
  photos?: Array<{
    height: number;
    width: number;
    html_attributions: string[];
    photo_reference: string;
    photo_url?: string;
    gcs_url?: string;
    optimized_urls?: {
      thumb?: string;
      large?: string;
    };
  }>;
  reviews?: Array<{
    author_name: string;
    rating: number;
    text: string;
    time: number;
  }>;
  
  // Walky specific fields
  campus_id?: string;
  categories?: string[];
  
  // Hierarchy fields
  parent_place_id?: string;
  child_place_ids?: string[];
  hierarchy_level?: number;
  hierarchy_path?: string[];
  is_inside_place?: boolean;
  contains_places?: boolean;
  place_category?: 'container' | 'standalone' | 'nested';
  floor_level?: string;
  
  // Status fields
  is_synced?: boolean;
  is_deleted?: boolean;
  deleted_at?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  last_synced_at?: Date;
  
  // Enhanced fields from backend
  nested_places_count?: number;
  has_nested_places?: boolean;
  photo_info?: {
    total_photos: number;
    synced_photos: number;
    thumbnails: Array<{
      thumb_url?: string;
      large_url?: string;
    }>;
  };
}

export interface PlacesFilters {
  campus_id?: string;
  search?: string;
  categories?: string[];
  place_category?: 'container' | 'standalone' | 'nested';
  hierarchy_level?: number;
  parent_place_id?: string | null;
  is_deleted?: boolean;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface PlacesResponse {
  places: Place[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export const placesAPI = {
  // Get all places
  getAllPlaces: async (filters: PlacesFilters = {}): Promise<PlacesResponse> => {
    try {
      const params = new URLSearchParams();
      
      if (filters.campus_id) params.append("campus_id", filters.campus_id);
      if (filters.search) params.append("search", filters.search);
      if (filters.categories?.length) params.append("categories", filters.categories.join(","));
      if (filters.place_category) params.append("place_category", filters.place_category);
      if (filters.hierarchy_level !== undefined) params.append("hierarchy_level", String(filters.hierarchy_level));
      if (filters.parent_place_id !== undefined) params.append("parent_place_id", filters.parent_place_id === null ? "" : filters.parent_place_id);
      if (filters.is_deleted !== undefined) params.append("is_deleted", String(filters.is_deleted));
      
      // Convert page to offset for backend
      if (filters.page && filters.limit) {
        const offset = (filters.page - 1) * filters.limit;
        params.append("offset", String(offset));
      }
      if (filters.limit) params.append("limit", String(filters.limit));
      if (filters.sort) params.append("sort", filters.sort);
      if (filters.order) params.append("order", filters.order);

      const response = await apiClient.get(`/api/admin/places?${params.toString()}`);

      // Handle success response with nested data structure
      if (response.data.success && response.data.data && response.data.data.places && Array.isArray(response.data.data.places)) {
        return {
          places: response.data.data.places,
          total: response.data.data.pagination?.total || 0,
          page: (response.data.data.pagination?.offset || 0) / (response.data.data.pagination?.limit || 20) + 1,
          pages: Math.ceil((response.data.data.pagination?.total || 0) / (response.data.data.pagination?.limit || 20)),
          limit: response.data.data.pagination?.limit || 20,
        };
      }

      // Handle pagination response structure (fallback)
      if (response.data.places && Array.isArray(response.data.places)) {
        return {
          places: response.data.places,
          total: response.data.total || 0,
          page: response.data.page || 1,
          pages: response.data.pages || 1,
          limit: response.data.limit || 20,
        };
      }

      // Handle simple array response
      if (Array.isArray(response.data)) {
        return {
          places: response.data,
          total: response.data.length,
          page: 1,
          pages: 1,
          limit: response.data.length,
        };
      }

      console.warn("⚠️ Unexpected response structure:", response.data);
      return {
        places: [],
        total: 0,
        page: 1,
        pages: 1,
        limit: 20,
      };
    } catch (error) {
      console.error("Failed to fetch places:", error);
      
      // Handle 404 specifically (no places found)
      if (error instanceof Error && "response" in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          console.log("📋 404 error - returning empty response");
          return {
            places: [],
            total: 0,
            page: 1,
            pages: 1,
            limit: 20,
          };
        }
      }
      
      throw error;
    }
  },

  // Update place categories
  updateCategories: async (placeId: string, categories: string[]): Promise<Place> => {
    const response = await apiClient.put(`/api/admin/places/${placeId}/categories`, { categories });
    return response.data;
  },

  // Delete place
  deletePlace: async (placeId: string): Promise<void> => {
    await apiClient.delete(`/api/admin/places/${placeId}`);
  },

  // Restore place
  restorePlace: async (placeId: string): Promise<Place> => {
    const response = await apiClient.post(`/api/admin/places/${placeId}/restore`);
    return response.data;
  },

  // Sync place photos
  syncPhotos: async (placeId: string): Promise<void> => {
    await apiClient.post(`/api/admin/places/${placeId}/sync-photos`);
  },

  // Batch sync photos
  batchSyncPhotos: async (placeIds: string[]): Promise<void> => {
    await apiClient.post("/api/admin/places/photo-sync/batch", { place_ids: placeIds });
  },

  // Get nested places
  getNestedPlaces: async (placeId: string, limit: number = 50, offset: number = 0): Promise<{
    parent_place: { place_id: string; name: string; place_category: string };
    nested_places: Place[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      has_more: boolean;
      current_page: number;
      total_pages: number;
    };
  }> => {
    const params = new URLSearchParams();
    params.append("limit", String(limit));
    params.append("offset", String(offset));

    const response = await apiClient.get(`/api/admin/places/${placeId}/nested?${params.toString()}`);
    
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    
    throw new Error("Invalid response structure");
  },

  // Popular places management
  getPopularPlaces: async (schoolId: string, campusId?: string): Promise<PopularPlace[]> => {
    const params = new URLSearchParams();
    params.append("school_id", schoolId);
    if (campusId) params.append("campus_id", campusId);

    const response = await apiClient.get(`/api/admin/places/popular?${params.toString()}`);
    return response.data.data || [];
  },

  setPopularPlaces: async (schoolId: string, places: { place_id: string; order: number }[], campusId?: string): Promise<PopularPlace[]> => {
    const response = await apiClient.post("/api/admin/places/popular", {
      school_id: schoolId,
      campus_id: campusId,
      places
    });
    return response.data.data || [];
  },

  removePopularPlace: async (popularPlaceId: string): Promise<void> => {
    await apiClient.delete(`/api/admin/places/popular/${popularPlaceId}`);
  },

  reorderPopularPlaces: async (updates: { id: string; order: number }[]): Promise<void> => {
    await apiClient.patch("/api/admin/places/popular/reorder", { updates });
  }
};

export interface PopularPlace {
  _id: string;
  school_id: string;
  campus_id?: string;
  place_id: Place | string;
  order: number;
  is_active: boolean;
  added_by?: {
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  added_at: Date;
  updated_at: Date;
}